/**
 * Hook for font loading with retry logic
 */

import { useState, useCallback, useEffect, useRef } from 'react'

import { detectFont, preloadFont } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/fonts'

/**
 * Hook to load and detect font availability
 *
 * @param {string} fontFamily - Font family to load
 * @returns {Object} { isLoaded, isLoading, error, retryLoadFont }
 */
export function useFontLoader(fontFamily) {
  const [error, setError] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // preloadFont retries over a budget, so a block torn down mid-load resolves into a component
  // that is no longer there
  const aliveRef = useRef(true)

  useEffect(
    () => () => {
      aliveRef.current = false
    },
    []
  )

  const loadFont = useCallback(async () => {
    if (!fontFamily) {
      setIsLoaded(true)
      return
    }

    // Check if already loaded
    if (detectFont(fontFamily)) {
      setIsLoaded(true)
      return
    }

    setError(null)
    setIsLoading(true)

    try {
      const success = await preloadFont(fontFamily)
      if (!aliveRef.current) return
      // Proceed regardless of success: if the font didn't load within the retryLoadFont
      // budget, the browser falls back to its default. Leaving isLoaded=false
      // would block Chart rendering forever (spec §11 edge case 5 / §12).
      setIsLoaded(true)
      if (!success) {
        setError(new Error(`Font "${fontFamily}" could not be loaded`))
      }
    } catch (error) {
      if (!aliveRef.current) return
      // Don't block the layout on font errors either — surface the error but
      // allow downstream rendering to proceed with the browser default.
      setError(error)
      setIsLoaded(true)
    } finally {
      if (aliveRef.current) setIsLoading(false)
    }
  }, [fontFamily])

  const retryLoadFont = useCallback(() => {
    setError(null)
    setIsLoaded(false)

    loadFont()
  }, [loadFont])

  useEffect(() => {
    loadFont()
  }, [loadFont])

  return { error, isLoaded, isLoading, retry: retryLoadFont }
}

export default useFontLoader
